// Pardoseala unui spațiu exterior ca geometrie: gresia, dalele de pe ploturi
// sau scândurile de deck ale unui balcon, ale unei logii sau ale unei terase,
// cu rostul dintre ele — din calepinajul scris de fațadă pe panoul `flooring`
// (`props["architecture.panel"].joints`, v. `fatada._calepinaj_pardoseala`),
// nu din convenții ale machetei. Separat de randor ca să poată fi PROBAT:
// unde stă fiecare piesă și încotro îi merge fibra se văd într-un test.
//
// Reperul pieselor: `a` de-a lungul laturii `lungul` a inelului, de la vârful
// `origine`; `b` perpendicular, spre interiorul inelului. Scris ca indici în
// inel, ca oglindirea «nord sus» și mutarea la originea locală (care ating
// numai geometria) să nu-l lase în urmă.
import * as THREE from "three";
import { insetInel } from "./panou3d";

/** Materialele de pardoseală cu desen propriu — aceleași chei ca în Blender
 *  (`TEXTURI_PARDOSEALA`) și în lista agentului (`fatada/prompt.py`). */
export const MATERIALE_PARDOSEALA = ["gresie_exterior", "dale_portelanate", "dale_terasa",
                                     "deck_compozit", "deck_lemn"] as const;

export interface Pardoseala {
  /** `tiles` (gresie lipită, rost umplut) | `raised` (dale pe ploturi, rost
   *  deschis) | `deck` (scânduri, rost deschis) */
  fel: string;
  origine: number;
  lungul: [number, number];
  /** pe ce axă stă latura lungă a piesei — încotro merge fibra scândurii */
  axaLunga: "a" | "b";
  /** `[a0, a1, b0, b1]` (celula: piesa + rostul) sau un poligon `[[a, b], …]` */
  piese: (number[] | number[][])[];
  rost: number;
  culoareRost: string;
  /** grosimea piesei */
  grosime: number;
}

/** Cât se vede din grosimea unei plăci de gresie peste chitul din rost. */
const GRESIE_VIZIBIL_M = 0.008;
/** Cât stă chitul sub fața gresiei. */
const CHIT_SUB_M = 0.002;
/** Cât se trage înăuntru tot ce e sub piese, ca nicio față a lui să nu cadă
 *  lângă (sub 1 cm) muchia unei piese de pe margine. */
export const PAT_RETRAS_M = 0.012;

export interface Reper { o: number[]; d: number[]; p: number[] }

/** Reperul pardoselii pe inelul panoului, sau `null` când indicii nu se potrivesc. */
export function reperul(inel: number[][], pd: Pardoseala): Reper | null {
  const o = inel[pd.origine], q0 = inel[pd.lungul[0]], q1 = inel[pd.lungul[1]];
  if (!o || !q0 || !q1) return null;
  const ex = q1[0] - q0[0], ey = q1[1] - q0[1], l = Math.hypot(ex, ey);
  if (l < 1e-6) return null;
  const d = [ex / l, ey / l];
  let p = [-d[1], d[0]];
  let cx = 0, cy = 0;
  for (const v of inel) { cx += v[0]; cy += v[1]; }
  cx /= inel.length; cy /= inel.length;
  if ((cx - o[0]) * p[0] + (cy - o[1]) * p[1] < 0) p = [-p[0], -p[1]];
  return { o, d, p };
}

/** Tonul unei piese: două piese vecine nu sunt niciodată perfect la fel. */
function tonul(a: number, b: number, variatie: number): number {
  const z = ((Math.sin(a * 12.9898 + b * 78.233) * 43758.5453) % 1 + 1) % 1;
  return 1 - variatie + 2 * variatie * z;
}

class Acumulator {
  poz: number[] = [];
  nrm: number[] = [];
  uv: number[] = [];
  col: number[] = [];
  tri(p: number[][], n: number[], uv: number[][], c: number): void {
    // înfășurat după normala cerută, oricum ar veni vârfurile
    const e1 = [p[1][0] - p[0][0], p[1][1] - p[0][1], p[1][2] - p[0][2]];
    const e2 = [p[2][0] - p[0][0], p[2][1] - p[0][1], p[2][2] - p[0][2]];
    const g = [e1[1] * e2[2] - e1[2] * e2[1], e1[2] * e2[0] - e1[0] * e2[2], e1[0] * e2[1] - e1[1] * e2[0]];
    const ord = g[0] * n[0] + g[1] * n[1] + g[2] * n[2] >= 0 ? [0, 1, 2] : [0, 2, 1];
    for (const i of ord) {
      this.poz.push(...p[i]); this.nrm.push(...n); this.uv.push(...uv[i]); this.col.push(c, c, c);
    }
  }
  geometrie(): THREE.BufferGeometry | null {
    if (!this.poz.length) return null;
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(this.poz, 3));
    g.setAttribute("normal", new THREE.Float32BufferAttribute(this.nrm, 3));
    g.setAttribute("uv", new THREE.Float32BufferAttribute(this.uv, 2));
    g.setAttribute("color", new THREE.Float32BufferAttribute(this.col, 3));
    return g;
  }
}

/** Piesele pardoselii (fețele de sus și canturile, fără fața de jos) cu fața
 *  de sus la `zSus`, și — la gresia lipită — patul de chit de sub ele. */
export function geometriaPardoselii(inel: number[][], pd: Pardoseala, zSus: number):
    { piese: THREE.BufferGeometry | null; pat: THREE.BufferGeometry | null } {
  const R = reperul(inel, pd);
  if (!R) return { piese: null, pat: null };
  const lumea = (a: number, b: number) => [R.o[0] + R.d[0] * a + R.p[0] * b, R.o[1] + R.d[1] * a + R.p[1] * b];
  const lipit = pd.fel === "tiles";
  // la gresie se vede doar ce iese peste chit; dalele și scândurile stau pe
  // ploturi, cu rostul deschis până la umbra de sub ele
  const t = lipit ? GRESIE_VIZIBIL_M : Math.max(0.01, pd.grosime - 0.001);
  const zJos = zSus - t;
  const variatie = pd.fel === "deck" ? 0.07 : 0.03;
  const r = Math.max(0.0005, pd.rost / 2);
  const acc = new Acumulator();
  // fibra: `u` în lungul laturii lungi a piesei, în metri
  const uvDe = (a: number, b: number) => (pd.axaLunga === "a" ? [a, b] : [b, a]);
  for (const pc of pd.piese) {
    let contur: number[][];
    if (typeof pc[0] === "number") {
      const [a0, a1, b0, b1] = pc as number[];
      if (a1 - a0 < 2 * r + 0.01 || b1 - b0 < 2 * r + 0.01) continue;
      contur = [[a0 + r, b0 + r], [a1 - r, b0 + r], [a1 - r, b1 - r], [a0 + r, b1 - r]];
    } else {
      contur = insetInel(pc as number[][], r);
      if (contur.length < 3) continue;
    }
    const c0 = contur[0];
    const ton = tonul(c0[0], c0[1], variatie);
    const cant = lipit ? ton * 0.85 : 0.28;
    // FAȚA DE SUS, triangulată în reperul pardoselii
    const tri = THREE.ShapeUtils.triangulateShape(contur.map(([a, b]) => new THREE.Vector2(a, b)), []);
    for (const [i, j, k] of tri) {
      const pts = [contur[i], contur[j], contur[k]];
      acc.tri(pts.map(([a, b]) => [...lumea(a, b), zSus]), [0, 0, 1], pts.map(([a, b]) => uvDe(a, b)), ton);
    }
    // CANTURILE, cu normala spre afara piesei
    let cx = 0, cy = 0;
    for (const [a, b] of contur) { cx += a; cy += b; }
    cx /= contur.length; cy /= contur.length;
    for (let i = 0; i < contur.length; i++) {
      const [a0, b0] = contur[i], [a1, b1] = contur[(i + 1) % contur.length];
      const la = Math.hypot(a1 - a0, b1 - b0);
      if (la < 1e-6) continue;
      let na = (b1 - b0) / la, nb = -(a1 - a0) / la;
      if ((a0 - cx) * na + (b0 - cy) * nb < 0) { na = -na; nb = -nb; }
      const n = [R.d[0] * na + R.p[0] * nb, R.d[1] * na + R.p[1] * nb, 0];
      const w0 = lumea(a0, b0), w1 = lumea(a1, b1);
      const P = [[...w0, zJos], [...w1, zJos], [...w1, zSus], [...w0, zSus]];
      const U = [[0, 0], [la, 0], [la, t], [0, t]];
      acc.tri([P[0], P[1], P[2]], n, [U[0], U[1], U[2]], cant);
      acc.tri([P[0], P[2], P[3]], n, [U[0], U[2], U[3]], cant);
    }
  }
  let pat: THREE.BufferGeometry | null = null;
  if (lipit) {
    // PATUL DE CHIT: inelul tras înăuntru, de sub gresie până la 2 mm sub fața ei
    const ring = insetInel(inel, PAT_RETRAS_M);
    if (ring.length >= 3) {
      const sh = new THREE.Shape(ring.map(([x, y]) => new THREE.Vector2(x, y)));
      const jos = zSus - pd.grosime;
      const g = new THREE.ExtrudeGeometry(sh, { depth: Math.max(0.002, pd.grosime - CHIT_SUB_M), bevelEnabled: false });
      g.translate(0, 0, jos);
      pat = g;
    }
  }
  return { piese: acc.geometrie(), pat };
}

/** FIBRA unei scânduri de deck: dungi lungi, puțin mai închise sau mai
 *  deschise, de-a lungul ei; fără rosturi — rosturile sunt adevărate, între
 *  scânduri. O repetare = 2 m în lung × 0,25 m în lat. */
let _fibra: THREE.Texture | null = null;
export function texturaFibra(): THREE.Texture | null {
  if (_fibra) return _fibra;
  if (typeof document === "undefined") return null;
  const c = document.createElement("canvas");
  c.width = 1024; c.height = 128;
  const ctx = c.getContext("2d");
  if (!ctx) return null;
  ctx.fillStyle = "rgb(236,236,236)";
  ctx.fillRect(0, 0, 1024, 128);
  for (let k = 0; k < 70; k++) {
    const y = Math.floor(((Math.sin(k * 91.7) * 43758.5) % 1 + 1) % 1 * 128);
    const v = 200 + Math.floor(((Math.sin(k * 17.3) * 9631.1) % 1 + 1) % 1 * 55);
    ctx.fillStyle = `rgb(${v},${v},${v})`;
    ctx.fillRect(0, y, 1024, 1 + (k % 3 === 0 ? 1 : 0));
  }
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(0.5, 4);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 8;
  _fibra = t;
  return t;
}
